import { useMemo } from "react"

import { Background, Handle, Position, ReactFlow, ReactFlowProvider } from "@xyflow/react"

import type { Edge, Node, NodeProps, NodeTypes } from "@xyflow/react"

import "@xyflow/react/dist/style.css"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { normalizeWorkflowEdgeHandles } from "../utils/workflow-edge-utils"

const NODE_STYLES: Record<
  string,
  { header: string; border: string; label: string; titleColor?: string }
> = {
  start: {
    header: CATEGORY_TONES[4],
    border: "border-border",
    label: "Start",
  },
  end: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "End",
  },
  conditional: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Conditional",
  },
  loopContainer: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Loop",
  },
  loopStart: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Loop Start",
  },
  loopEnd: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Loop End",
  },
  loop: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Loop",
  },
  parallel: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Parallel",
  },
  merge: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Merge",
  },
  delay: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Delay",
  },
  llm: {
    header: CATEGORY_TONES[1],
    border: "border-border",
    label: "LLM Processor",
  },
  agent: {
    header: CATEGORY_TONES[1],
    border: "border-border",
    label: "Agent",
  },
  variable: {
    header: CATEGORY_TONES[2],
    border: "border-border",
    label: "Variable",
  },
  tool: {
    header: CATEGORY_TONES[0],
    border: "border-border",
    label: "Tool",
  },
  http: {
    header: CATEGORY_TONES[0],
    border: "border-border",
    label: "HTTP Request",
  },
  retry: {
    header: CATEGORY_TONES[3],
    border: "border-border",
    label: "Retry",
  },
}

const DEFAULT_NODE_STYLE = {
  header: "bg-muted/50",
  border: "border-border",
  label: "Node",
  titleColor: undefined as string | undefined,
}

function MiniNode({ data }: NodeProps) {
  const nodeType = (data.originalType as string) || "llm"
  const style = nodeType in NODE_STYLES ? NODE_STYLES[nodeType] : DEFAULT_NODE_STYLE
  const label = (data.label as string) || style.label

  return (
    <div
      className={cn(
        "bg-card relative flex min-w-[240px] flex-col overflow-hidden rounded-xl border-2 shadow-lg",
        style.border
      )}
    >
      <Handle className="opacity-0" position={Position.Left} type="target" />
      {/* Header - matching the actual node CardHeader style */}
      <div className={cn("rounded-t-[10px] px-5 py-3", style.header)}>
        <span className={cn("text-base font-semibold", style.titleColor)}>{label}</span>
      </div>
      {/* Body - simulating CardContent */}
      <div className="bg-card flex h-12 items-center justify-center px-5">
        <div className="bg-muted h-2.5 w-full rounded-full" />
      </div>
      <Handle className="opacity-0" position={Position.Right} type="source" />
    </div>
  )
}

const miniNodeTypes: NodeTypes = {
  mini: MiniNode,
}

type WorkflowContent = {
  nodes?: Node[]
  edges?: Edge[]
}

type WorkflowMiniPreviewProps = {
  content: string | WorkflowContent
  className?: string
}

function parseWorkflowContent(content: string | WorkflowContent): { nodes: Node[]; edges: Edge[] } {
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content) as WorkflowContent
      return {
        nodes: parsed.nodes || [],
        edges: parsed.edges || [],
      }
    } catch {
      return { nodes: [], edges: [] }
    }
  }
  return {
    nodes: content.nodes || [],
    edges: content.edges || [],
  }
}

function MiniPreviewCanvas({ content }: { content: string | WorkflowContent }) {
  const { nodes: originalNodes, edges: originalEdges } = useMemo(
    () => parseWorkflowContent(content),
    [content]
  )

  const miniNodes: Node[] = useMemo(() => {
    return originalNodes.map((node) => ({
      ...node,
      type: "mini",
      data: {
        ...node.data,
        originalType: node.type,
      },
    }))
  }, [originalNodes])

  const miniEdges: Edge[] = useMemo(() => {
    return originalEdges.map((edge) => {
      const { sourceHandle, targetHandle, ...normalizedEdge } = normalizeWorkflowEdgeHandles(edge)
      void sourceHandle
      void targetHandle

      return {
        ...normalizedEdge,
        // Force the default edge: this preview registers no `edgeTypes`, so a
        // custom type (e.g. "labeled") would log a "type not found" warning per
        // edge (hundreds across the card grid). The thumbnail only needs plain
        // lines, and an edge label would be illegible at thumbnail scale anyway.
        type: undefined,
        label: undefined,
        style: { stroke: "hsl(var(--muted-foreground))", strokeWidth: 2, opacity: 0.6 },
      }
    })
  }, [originalEdges])

  if (miniNodes.length === 0) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center text-[10px]">
        Empty
      </div>
    )
  }

  return (
    <ReactFlow
      fitView
      edges={miniEdges}
      fitViewOptions={{ padding: 0.15 }}
      maxZoom={1}
      minZoom={0.1}
      nodes={miniNodes}
      nodesConnectable={false}
      nodesDraggable={false}
      nodesFocusable={false}
      nodeTypes={miniNodeTypes}
      panOnDrag={false}
      panOnScroll={false}
      proOptions={{ hideAttribution: true }}
      selectionOnDrag={false}
      zoomOnDoubleClick={false}
      zoomOnPinch={false}
      zoomOnScroll={false}
    >
      <Background className="opacity-20" gap={20} size={1} />
    </ReactFlow>
  )
}

export function WorkflowMiniPreview({ content, className }: WorkflowMiniPreviewProps) {
  return (
    <div className={cn("h-full w-full overflow-hidden", className)}>
      <ReactFlowProvider>
        <MiniPreviewCanvas content={content} />
      </ReactFlowProvider>
    </div>
  )
}
